import { AnyAction } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import { App, ValueLabelElement } from '@ps20-frontend/frontend-models';
import {
  CheckoutProdukt,
  GlobalActions,
  ProduktDeleted,
  TkvCheckoutProduktPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  Diagnose,
  Domaene,
  EinsatzgebieteTier,
  KennzeichnungsArt,
  Tierart,
  Tiergeschlecht,
  Tierhaltung,
} from '@ps20/bff-models';
import { IAppState } from '@ps20-frontend/apps/checkout-store';
import { CheckoutActions } from '../checkout.actions';
import {
  TkvAngabenPayload,
  TkvDiagnoseDeleteItemPayload,
  TkvDiagnosePayload,
  TkvEinsatzgebietPayload,
  TkvKennzeichnungsArtPayload,
  TkvTierarztBesuchtPayload,
} from '../action-types/tkvAngabenPayload';

export interface TkvAngaben {
  angabenListe: TkvAngabenElement[];
  diagnosen?: Diagnose[];
  diagnosenDetail?: Domaene[];
}

export interface TkvAngabenElement {
  angebotsId: string;
  name: string;
  einsatzgebiet?: EinsatzgebieteTier;
  kennzeichnungsArt: Kennzeichnungen;
  kennzeichnung?: string;
  inBehandlung: boolean;
  diagnoseAngaben: Array<DiagnoseAngaben>;
  istBeiTierarzt: boolean;
  rasse: ValueLabelElement;
  geburtsdatum: string;
  geschlecht: Tiergeschlecht;
  tierart: Tierart;
  disabledFields: DisabledFields;
  tierhaltung?: Tierhaltung;
  isTierKastriert?: boolean;
}

export interface DisabledFields {
  name: boolean;
  kennzeichnung: boolean;
}

export enum Kennzeichnungen {
  CHIP = 'CHIP',
  TAETOWIERUNG = 'TAETOWIERUNG',
  LEBENSNUMMER = 'LEBENSNUMMER',
  KEINE = 'KEINE',
}

export interface DiagnoseAngaben {
  gewaehlteDiagnose: Diagnose;
  gewaehlteDiagnoseDetail: Domaene;
  diagnoseBeschreibung: string;
}

const resetDiagnosen = function (): Array<DiagnoseAngaben> {
  return [
    {
      gewaehlteDiagnose: {
        wert: '',
        text: '',
        detail: null,
      },
      gewaehlteDiagnoseDetail: {
        wert: '',
        text: '',
      },
      diagnoseBeschreibung: '',
    },
  ];
};

const findAngabenIndex = (sectionState: TkvAngaben, angebotsId: string): number | null => {
  const index = sectionState.angabenListe.findIndex(elem => elem.angebotsId === angebotsId);
  return index >= 0 ? index : null;
};

export const TKV_TIERNAME_REGEXP = String.raw`^[a-zäöüÄÖÜßéèêàáâ#:?!´/=+@$(),A-Z0-9.'\- ]{0,50}$`;
export const TKV_LEBENSNUMMER_REGEXP = '^[A-Za-z0-9äöüÄÖÜß ]{1,15}$';
export const TKV_CHIP_REGEXP = String.raw`^\d{15}$`;
export const TKV_TAETOWIERUNG_REGEXP = '^[A-Za-z0-9äöüÄÖÜß]{1,20}$';

export const tkvAngabenReducer = function (
  sectionState: TkvAngaben = null,
  action: AnyAction,
  state?: IAppState,
): TkvAngaben {
  switch (action.type) {
    case GlobalActions.PRODUKT_ACTION: {
      const produkt: CheckoutProdukt = action.payload;
      if (produkt.appId !== App.TKV) {
        return sectionState;
      }
      const newState: TkvAngaben = cloneDeep(sectionState);
      const actionPayload: TkvCheckoutProduktPayload = action.payload;
      const existingTkvAngabenElement: TkvAngabenElement = sectionState.angabenListe.find(
        item => item.angebotsId === actionPayload.angebotsId,
      );

      //ToDo: can fields be disabled if name and kennzeichnung are set here
      const kennzeichnungsData =
        actionPayload.tierart === existingTkvAngabenElement?.tierart
          ? {}
          : {
              einsatzgebiet: undefined,
              kennzeichnung: undefined,
              name: produkt.beibehalten ? 'unbekannt' : null,
              kennzeichnungsArt: null,
            };

      const diagnoseAngaben =
        actionPayload.inBehandlung === existingTkvAngabenElement?.inBehandlung
          ? {}
          : { diagnoseAngaben: resetDiagnosen() };

      const newAngaben = {
        ...existingTkvAngabenElement,
        ...kennzeichnungsData,
        ...diagnoseAngaben,
        angebotsId: actionPayload.angebotsId,
        tierart: actionPayload.tierart,
        rasse: actionPayload.rasse,
        geburtsdatum: actionPayload.geburtsdatumTier,
        geschlecht: actionPayload.geschlecht,
        tierhaltung: actionPayload.tierhaltung,
        inBehandlung: actionPayload.inBehandlung,
        isTierKastriert: actionPayload.isTierKastriert,
      };

      if (actionPayload.tiername && !newAngaben.name) {
        newAngaben.name = actionPayload.tiername;
      }
      if (actionPayload.kennzeichen && !newAngaben.kennzeichnung) {
        newAngaben.kennzeichnung = actionPayload.kennzeichen;
      }
      if (actionPayload.einsatzgebiet && !newAngaben.einsatzgebiet) {
        newAngaben.einsatzgebiet = actionPayload.einsatzgebiet;
      }
      if (actionPayload.kennzeichnungsArt && !newAngaben.kennzeichnungsArt) {
        newAngaben.kennzeichnungsArt = convertToFrontendKennzeichnungen(
          actionPayload.kennzeichnungsArt,
        );
      }

      const isErsatz =
        actionPayload.vorvertragsnummer && actionPayload.vorvertragsnummer.length > 0;
      const isNotBeibehaltenAndIsErsatzgeschaeft = isErsatz && !actionPayload?.beibehalten;

      newAngaben.disabledFields = {
        name: !!(
          actionPayload.tiername &&
          new RegExp(TKV_TIERNAME_REGEXP).exec(actionPayload.tiername) !== null &&
          isNotBeibehaltenAndIsErsatzgeschaeft &&
          state.vnWechselAngaben.angabenListe.length === 0
        ),
        kennzeichnung: !!(
          actionPayload.kennzeichnungsArt !== KennzeichnungsArt.KEINE &&
          actionPayload.kennzeichen &&
          isNotBeibehaltenAndIsErsatzgeschaeft &&
          kennzeichenMatchRegExp(actionPayload.kennzeichnungsArt, actionPayload.kennzeichen)
        ),
      };

      const index = sectionState.angabenListe.findIndex(
        elem => elem.angebotsId === actionPayload.angebotsId,
      );
      if (index >= 0) {
        newState.angabenListe[index] = newAngaben;
      } else {
        newState.angabenListe.push(newAngaben);
      }

      return newState;
    }
    case GlobalActions.PRODUKT_DELETED: {
      const produktDeletedPayload: ProduktDeleted = action.payload;
      const newState: TkvAngaben = cloneDeep(sectionState);

      for (const angebot of produktDeletedPayload.angebotsIds.filter(
        angebotsId => angebotsId.appId === App.TKV,
      )) {
        const index = newState.angabenListe.findIndex(
          elem => elem.angebotsId === angebot.angebotsId,
        );
        if (index >= 0) {
          newState.angabenListe.splice(index, 1);
        }
      }

      return newState;
    }
    case CheckoutActions.DIAGNOSE_LIST_RECEIVED: {
      return {
        ...sectionState,
        diagnosen: action.payload,
      };
    }
    case CheckoutActions.DIAGNOSE_DETAILS_LIST_RECEIVED: {
      return {
        ...sectionState,
        diagnosenDetail: action.payload,
      };
    }
    case CheckoutActions.TIERARZT_BESUCHT_CHANGED: {
      const payload: TkvTierarztBesuchtPayload = action.payload;
      const newState: TkvAngaben = cloneDeep(sectionState);
      const index = findAngabenIndex(sectionState, payload.angebotsId);
      if (index !== null) {
        newState.angabenListe[index].istBeiTierarzt = payload.value;
        newState.angabenListe[index].diagnoseAngaben = resetDiagnosen();
      }

      return newState;
    }
    case CheckoutActions.DIAGNOSE_CHANGED: {
      const payload: TkvDiagnosePayload = action.payload;
      const angabenIndex = findAngabenIndex(sectionState, payload.angebotsId);
      if (angabenIndex === null) {
        return sectionState;
      }
      const newState: TkvAngaben = cloneDeep(sectionState);
      newState.angabenListe[angabenIndex].diagnoseAngaben[payload.index].gewaehlteDiagnose =
        newState.diagnosen.find(elem => elem.wert === payload.value);
      newState.angabenListe[angabenIndex].diagnoseAngaben[payload.index].diagnoseBeschreibung = '';
      newState.angabenListe[angabenIndex].diagnoseAngaben[payload.index].gewaehlteDiagnoseDetail = {
        text: '',
        wert: '',
      };

      return newState;
    }
    case CheckoutActions.DIAGNOSE_DETAIL_CHANGED: {
      const payload: TkvDiagnosePayload = action.payload;
      const index = findAngabenIndex(sectionState, payload.angebotsId);
      if (index === null) {
        return sectionState;
      }
      const newState: TkvAngaben = cloneDeep(sectionState);
      newState.angabenListe[index].diagnoseAngaben[payload.index].gewaehlteDiagnoseDetail =
        newState.diagnosenDetail.find(elem => elem.wert === payload.value);

      return newState;
    }
    case CheckoutActions.DIAGNOSE_BESCHREIBUNG_CHANGED: {
      const payload: TkvDiagnosePayload = action.payload;
      const index = findAngabenIndex(sectionState, payload.angebotsId);
      if (index === null) {
        return sectionState;
      }
      const newState: TkvAngaben = cloneDeep(sectionState);
      newState.angabenListe[index].diagnoseAngaben[payload.index].diagnoseBeschreibung =
        payload.value;

      return newState;
    }
    case CheckoutActions.DIAGNOSE_ITEM_DELETE: {
      const payload: TkvDiagnoseDeleteItemPayload = action.payload;
      const index = findAngabenIndex(sectionState, payload.angebotsId);
      if (index === null) {
        return sectionState;
      }
      const newState: TkvAngaben = cloneDeep(sectionState);
      newState.angabenListe[index].diagnoseAngaben.splice(payload.itemIndex, 1);

      return newState;
    }
    case CheckoutActions.DIAGNOSE_ITEM_ADD: {
      const payload: string = action.payload;
      const index = findAngabenIndex(sectionState, payload);
      if (index === null) {
        return sectionState;
      }
      const newState = cloneDeep(sectionState);
      newState.angabenListe[index].diagnoseAngaben.push({
        gewaehlteDiagnose: {
          wert: '',
          text: '',
          detail: null,
        },
        gewaehlteDiagnoseDetail: {
          wert: '',
          text: '',
        },
        diagnoseBeschreibung: '',
      });

      return newState;
    }
    case CheckoutActions.TKV_TIERNAME_CHANGED: {
      const payload: TkvAngabenPayload = action.payload;
      const index = findAngabenIndex(sectionState, payload.angebotsId);
      if (index === null) {
        return sectionState;
      }
      const newState: TkvAngaben = cloneDeep(sectionState);
      newState.angabenListe[index].name = payload.value;

      return newState;
    }
    case CheckoutActions.TKV_EINSATZGEBIET_CHANGED: {
      const payload: TkvEinsatzgebietPayload = action.payload;
      const index = findAngabenIndex(sectionState, payload.angebotsId);
      if (index === null) {
        return sectionState;
      }
      const newState: TkvAngaben = cloneDeep(sectionState);
      newState.angabenListe[index].einsatzgebiet = payload.value;

      return newState;
    }
    case CheckoutActions.TKV_KENNZEICHNUNGSART_CHANGED: {
      const payload: TkvKennzeichnungsArtPayload = action.payload;
      const index = findAngabenIndex(sectionState, payload.angebotsId);
      if (index === null) {
        return sectionState;
      }
      const newState: TkvAngaben = cloneDeep(sectionState);
      newState.angabenListe[index].kennzeichnungsArt = payload.value;
      newState.angabenListe[index].kennzeichnung = '';

      return newState;
    }
    case CheckoutActions.TKV_KENNZEICHNUNG_CHANGED: {
      const payload: TkvAngabenPayload = action.payload;
      const index = findAngabenIndex(sectionState, payload.angebotsId);

      if (index === null) {
        return sectionState;
      }

      const newState: TkvAngaben = cloneDeep(sectionState);
      newState.angabenListe[index].kennzeichnung = payload.value;

      return newState;
    }
    default:
      return sectionState;
  }
};

function convertToFrontendKennzeichnungen(kennzeichnungsArt: KennzeichnungsArt): Kennzeichnungen {
  for (const key in Kennzeichnungen) {
    if ((Kennzeichnungen as Record<string, unknown>)[key] === kennzeichnungsArt) {
      return (Kennzeichnungen as Record<string, Kennzeichnungen>)[key];
    }
  }
  return null;
}

function kennzeichenMatchRegExp(
  kennzeichnungsArt: KennzeichnungsArt,
  kennzeichen: string,
): boolean {
  switch (kennzeichnungsArt) {
    case KennzeichnungsArt.CHIP:
      return new RegExp(TKV_CHIP_REGEXP).exec(kennzeichen) !== null;
    case KennzeichnungsArt.LEBENSNUMMER:
      return new RegExp(TKV_LEBENSNUMMER_REGEXP).exec(kennzeichen) !== null;
    case KennzeichnungsArt.TAETOWIERUNG:
      return new RegExp(TKV_TAETOWIERUNG_REGEXP).exec(kennzeichen) !== null;
    default:
      return true;
  }
}
